import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type TeacherRecord } from '@/mocks/teachers';
import { students } from '@/mocks/students';

interface TeacherTableProps {
  teachers: TeacherRecord[];
  onRowClick?: (teacher: TeacherRecord) => void;
  onClassClick?: (className: string) => void;
  onStatusChange?: (teacher: TeacherRecord, newStatus: string) => void;
}

export default function TeacherTable({ teachers, onRowClick, onClassClick, onStatusChange }: TeacherTableProps) {
  const { t } = useTranslation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!confirmingId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (confirmRef.current && !confirmRef.current.contains(e.target as Node)) {
        setConfirmingId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [confirmingId]);

  const handleConfirmReactivate = (teacher: TeacherRecord) => {
    setConfirmingId(null);
    onStatusChange?.(teacher, 'active');
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/60">
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.number')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.teacher')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.role')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.class')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.students')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.joiningDate')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.contact')}</th>
              <th className="text-left px-5 py-3.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">{t('teachers.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200">
            {teachers.map((teacher, idx) => {
              const isConfirming = confirmingId === teacher.id;
              return (
                <tr
                  key={teacher.id}
                  className="hover:bg-background-100/50 transition-colors cursor-pointer"
                  onClick={() => onRowClick?.(teacher)}
                >
                  <td className="px-5 py-3.5 text-foreground-400 font-mono text-xs whitespace-nowrap">{idx + 1}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-label font-bold text-primary-700">
                          {teacher.name.split(' ').filter(w => w[0] !== 'D' || w !== 'Dr.').slice(0, 2).map(w => w[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-label font-medium text-foreground-800">{teacher.name}</p>
                        <p className="text-xs text-foreground-400 font-mono">{teacher.teacherId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-label font-medium bg-secondary-100 text-secondary-700">{teacher.role}</span>
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    {onClassClick ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onClassClick(teacher.className); }}
                        className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors cursor-pointer"
                      >
                        <i className="ri-group-line text-sm" />
                        {teacher.className}
                      </button>
                    ) : (
                      <span className="text-sm text-foreground-700 font-label">{teacher.className}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-semibold bg-accent-100 text-accent-700">
                      <i className="ri-user-line text-[10px]" />
                      {students.filter((s) => s.classTeacher === teacher.name).length}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-foreground-600 font-label whitespace-nowrap">{teacher.dateOfJoining}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <div>
                      <span className="text-sm text-foreground-700 font-mono">{teacher.contactNumber}</span>
                      <p className="text-xs text-foreground-400">{teacher.email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium ${
                          teacher.status === 'active' ? 'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30' :
                          teacher.status === 'on-leave' ? 'bg-semantic-warning-bg text-foreground-800 border border-semantic-warning/30' :
                          'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            teacher.status === 'active' ? 'bg-semantic-success' :
                            teacher.status === 'on-leave' ? 'bg-semantic-warning' : 'bg-semantic-error'
                          }`} />
                          {teacher.status === 'active' ? t('teachers.active') : teacher.status === 'on-leave' ? t('teachers.onLeave') : t('teachers.inactive')}
                        </span>
                        {teacher.status === 'inactive' && onStatusChange && (
                          <div className="relative" ref={isConfirming ? confirmRef : undefined}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setConfirmingId(isConfirming ? null : teacher.id); }}
                              className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isConfirming ? 'bg-semantic-success text-white' : 'bg-semantic-success-bg text-semantic-success hover:bg-semantic-success/20'}`}
                              title={isConfirming ? t('students.confirmReactivation') : t('teachers.reactivateTeacher')}
                            >
                              <i className={`text-xs ${isConfirming ? 'ri-check-line' : 'ri-refresh-line'}`} />
                            </button>
                            {isConfirming && (
                              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-40 animate-[modalEnter_150ms_cubic-bezier(0.4,0,0.2,1)]">
                                <div className="bg-background-50 rounded-lg border border-background-200 shadow-floating px-3 py-2 flex items-center gap-2 whitespace-nowrap">
                                  <span className="text-xs font-label font-medium text-foreground-700">{t('teachers.reactivateConfirm', { name: teacher.name.split(' ')[0] })}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleConfirmReactivate(teacher); }}
                                    className="px-2 py-0.5 text-xs font-label font-semibold rounded bg-semantic-success text-white hover:bg-semantic-success/90 transition-colors cursor-pointer"
                                  >
                                    {t('common.yes')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setConfirmingId(null); }}
                                    className="px-2 py-0.5 text-xs font-label font-semibold rounded bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer"
                                  >
                                    {t('common.no')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {teacher.status === 'inactive' && teacher.deactivatedAt && (
                        <p className="text-[10px] text-foreground-400 mt-1 font-label whitespace-nowrap">
                          {new Date(teacher.deactivatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {teachers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-foreground-400">
          <i className="ri-user-search-line text-4xl mb-3" />
          <p className="text-sm font-label font-medium">{t('teachers.noTeachersFound')}</p>
          <p className="text-xs font-label mt-1">{t('common.tryAdjustingFilters')}</p>
        </div>
      )}
    </div>
  );
}